/**
 * /experiencias — full Pass offers explorer.
 * Uses MarketplaceLayout so the header / footer / cart match the rest of marketplace.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sparkles, Gift, MapPin, Search, ArrowRight } from 'lucide-react';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { passPublic } from '../../services/passApi';

const CATEGORIES = [
  ['', 'Todas'],
  ['restaurant', 'Restaurantes'],
  ['yacht', 'Yates'],
  ['tour', 'Tours'],
  ['nightlife', 'Antros'],
  ['hotel_daypass', 'Day Pass'],
  ['beach_club', 'Beach Clubs'],
  ['jewelry', 'Joyerías'],
  ['vip_transport', 'Transporte VIP'],
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.filter(([v]) => v).map(([v, l]) => [v, l]));

export default function ExperiencesPage() {
  const [offers, setOffers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    passPublic.cities().then((res) => setCities(res.data.cities || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { limit: 60 };
    if (city) params.city = city;
    if (category) params.category = category;
    if (debouncedSearch) params.search = debouncedSearch;
    passPublic
      .marketplaceOffers(params)
      .then((res) => alive && setOffers(res.data.offers || []))
      .catch(() => alive && setOffers([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [city, category, debouncedSearch]);

  const counters = useMemo(() => {
    const map = {};
    for (const o of offers) map[o.category] = (map[o.category] || 0) + 1;
    return map;
  }, [offers]);

  return (
    <MarketplaceLayout>
      <Helmet>
        <title>Experiencias y promociones — Todo en Local Pass</title>
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-500/20 text-teal-200 text-[11px] font-semibold mb-3">
            <Sparkles className="w-3 h-3" /> Todo en Local Pass
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Experiencias y promociones locales</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            Restaurantes, yates, tours, antros, joyerías y más — recomendados por locales en tu ciudad.
          </p>

          {/* Search */}
          <div className="mt-6 relative max-w-xl">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar (ej. margarita, yate, day pass)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-slate-400 focus:outline-none focus:bg-white/20"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          {cities.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <Chip label="Todas las ciudades" active={!city} onClick={() => setCity('')} icon={MapPin} />
              {cities.map((c) => (
                <Chip key={c} label={c} active={city === c} onClick={() => setCity(c)} icon={MapPin} />
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(([v, l]) => (
              <Chip
                key={v || 'all'}
                label={l}
                active={category === v}
                onClick={() => setCategory(v)}
                count={v && counters[v]}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 mt-4 mb-10">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl h-64 animate-pulse border border-slate-100" />
            ))}
          </div>
        ) : offers.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-500 border border-slate-100">
            Sin experiencias para esos filtros. Prueba otra ciudad o categoría.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {offers.map((o) => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </div>
        )}
      </div>
    </MarketplaceLayout>
  );
}

function Chip({ label, active, onClick, icon: Icon, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
        active
          ? 'bg-slate-900 text-white'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />} {label}
      {typeof count === 'number' && count > 0 && (
        <span className={`ml-1 text-[10px] ${active ? 'text-white/70' : 'text-slate-500'}`}>{count}</span>
      )}
    </button>
  );
}

function OfferCard({ offer }) {
  const benefit = offer.customer_benefits?.[0]?.name;
  return (
    <Link
      to={`/pass/oferta/${offer.slug}`}
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="aspect-[4/3] bg-slate-100 relative">
        {offer.images?.[0] ? (
          <img src={offer.images[0]} alt={offer.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Gift className="w-10 h-10" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wide">
          {CATEGORY_LABEL[offer.category] || offer.category}
        </span>
        {offer.city && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] inline-flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5" /> {offer.city}
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">{offer.title}</h3>
        {offer.business_name && (
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{offer.business_name}</p>
        )}
        {benefit && <p className="text-xs text-teal-600 font-medium mt-1.5 line-clamp-1">{benefit}</p>}
        <div className="mt-auto pt-3 inline-flex items-center gap-1 text-orange-500 text-xs font-semibold">
          Ver detalles <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}
